// External libraries
import React from 'react';
import { useStoreState } from '@/store';
import { getLocale } from '../globals';
import { Flag } from '@paypalcorp/pp-react-flag-weasley-v7';

type Country = {
  // This should be CountryCodes but it is nullable in our graph
  countryCode: string;
  countryName: string;
  key: string;
};

type DecoratedOption = {
  decoration: JSX.Element;
  key: string;
  primaryText: string;
};

export const useNationalityOptions = (): [
  Country[],
  DecoratedOption[],
  string,
] => {
  // Retrive country information based on locale
  const { locale } = getLocale();
  const countryList =
    useStoreState(({ api }) => api.griffinMetadata.territories[locale]) || [];
  const nationality =
    useStoreState(({ form }) => form?.nationality?.value) || '';

  // Map country list into country options
  // Memoize the options so that they are not recalculated unnecessarily
  const [countryOptions, decoratedOptions] = React.useMemo(() => {
    const countries = countryList.map(({ key, value, text }) => ({
      countryCode: value || '',
      countryName: text || '',
      key: key || '',
    }));

    const decorated = countryList.map(({ key, value, text }) => ({
      decoration: <Flag countryCode={value} size="md" />,
      key: key || '',
      primaryText: text || '',
    }));

    return [countries, decorated];
  }, [locale, countryList]);

  return [countryOptions, decoratedOptions, nationality];
};
